import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button, CloseIcon, Lock2Icon, Tooltip } from '@blade/ui-kit'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { WalletsDialog } from '@root/widgets/desktop/wallets-dialog'

import { DestinationWalletNodeDefs } from '../../model/nodes'

import { NodeBase } from './common/nodes-common'
import { WalletDestinationAmount } from './wallet-destination/wallet-destination-amount'

import type { DistributeFundsModel } from '../../atoms'
import type { DistributeTargetModel } from '../../atoms/reatom-distribute-target'
import type { StyleProps } from '@root/shared/react'

type Props = {
  target: DistributeTargetModel
  model: DistributeFundsModel
  ref?: React.Ref<HTMLDivElement>
} & StyleProps

export const WalletDestinationNode: React.FC<Props> = ({
  target,
  model,
  style,
  ref,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const wallet = target.wallet
  const toggleTarget = useAction(model.toggleTarget)
  const [inputSource] = useAtom(target.inputSource)
  const unlock = useAction(target.unlock)

  return (
    <>
      <Root style={style} ref={ref}>
        <Header>
          <ShortStyled size="xs" wallet={wallet} />
          {inputSource === 'auto' ? undefined : (
            <Tooltip
              css={css`
                width: 300px;
              `}
              content={t('wallet.distribute.unlock-tooltip')}
            >
              <LockIcon size={14} onClick={() => unlock()} />
            </Tooltip>
          )}
          {/* <NodeRefreshButton
            onClick={() => {
              setOpen(true)
            }}
          /> */}
        </Header>
        <WalletDestinationAmount target={target} model={model} />
        <CloseButton
          onClick={() => {
            toggleTarget(target.wallet)
          }}
          size="h20"
          variant="soft"
          iconLeft={<CloseIcon />}
        />
      </Root>
      <WalletsDialog
        open={open}
        onOpenChange={setOpen}
        selectedWalletId={undefined}
        onWalletSelected={(wallet) => {
          toggleTarget(wallet)
          setOpen(false)
        }}
      />
    </>
  )
}

const Root = styled(NodeBase)`
  padding-inline: 3px;
  padding-block: 3px;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;

  height: ${DestinationWalletNodeDefs.height}px;
  width: ${DestinationWalletNodeDefs.width}px;
  position: relative;
`

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  justify-content: space-between;
  padding-inline: 5px;
  gap: 1px;
`

const ShortStyled = styled(WalletModelShort)`
  --wallet-short-indicator-size: 3px;
  font-size: 11px;
  font-weight: 600;
`

const CloseButton = styled(Button)`
  position: absolute;
  right: -10px;
  top: -10px;
  opacity: 0;
  transition: opacity 0.15s linear;

  ${Root}:hover & {
    opacity: 1;
  }
`

const LockIcon = styled(Lock2Icon)`
  cursor: pointer;
  color: var(--red-9);
  transition: color 0.15s linear;
  &:hover {
    color: var(--red-10);
  }
`
